import React, { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { FiPlus } from 'react-icons/fi';

const ServicesPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });
  const [hoveredPanel, setHoveredPanel] = useState(null);

  const services = [
    {
      id: 'asset-management',
      title: 'Aviation Asset Management',
      verb: 'Manage',
      description: 'Optimizing aviation asset value across its lifecycle',
      image: 'https://advancedflight.fastly.deploy.net.nz/advancedflight-prod/images/Heli-management/_large/1452/H130-IJV-LW-Mechanics.webp',
      color: 'from-blue-600/80 to-cyan-600/80'
    },
    {
      id: 'airworthiness',
      title: 'Airworthiness Oversight',
      verb: 'Ensure',
      description: 'Regulatory compliance and safety certification standards',
      image: 'https://images.unsplash.com/photo-1543861596-9a8035ab6cad?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTI0fHxoZWxpY29wdGVyfGVufDB8fDB8fHww',
      color: 'from-emerald-600/80 to-teal-600/80'
    },
    {
      id: 'project-execution',
      title: 'Project Execution & Coordination',
      verb: 'Execute',
      description: 'Timely delivery with precision project management',
      image: 'https://advancedflight.fastly.deploy.net.nz/advancedflight-prod/images/Heli-management/_large/356/H130-LW-Brooke.webp',
      color: 'from-purple-600/80 to-indigo-600/80'
    },
    {
      id: 'maintenance',
      title: 'Maintenance & Component Overhaul',
      verb: 'Maintain',
      description: 'Technical excellence in aircraft maintenance solutions',
      image: 'https://images.unsplash.com/photo-1616899822079-5e40267ebbc4?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mzd8fGhlbGljb3B0ZXJ8ZW58MHx8MHx8fDA%3D',
      color: 'from-orange-600/80 to-red-600/80'
    },
    {
      id: 'relocation',
      title: 'Global Aircraft Relocation',
      verb: 'Relocate',
      description: 'Worldwide transfer with logistical precision',
      image: 'https://images.unsplash.com/photo-1629155714099-8c1b4a515e98?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OTd8fGhlbGljb3B0ZXJ8ZW58MHx8MHx8fDA%3D',
      color: 'from-pink-600/80 to-rose-600/80'
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-cloud-beige via-white to-cloud-beige">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-midnight-navy mb-8 tracking-tight font-['Montserrat',_sans-serif]">
            Our <span className="bg-gradient-to-r from-aero-blue to-cyan-400 bg-clip-text text-transparent">Services</span>
          </h2>
          <p className="text-xl text-shadow-gray max-w-3xl mx-auto leading-relaxed font-light">
            Comprehensive aviation solutions designed to keep your aircraft mission-ready 
            with precision, reliability, and safety at the forefront of everything we do.
          </p>
        </motion.div>

        {/* Cinematic Horizontal Services Layout */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="relative h-[600px] bg-black rounded-2xl overflow-hidden shadow-2xl"
        >
          <div className="flex h-full">
            {services.map((service, index) => {
              const isHovered = hoveredPanel === service.id;
              const isOthersHovered = hoveredPanel && hoveredPanel !== service.id;
              
              return (
                <motion.div
                  key={service.id}
                  className="relative overflow-hidden cursor-pointer"
                  animate={{
                    flex: isHovered ? 2 : 1,
                    filter: isOthersHovered ? 'brightness(0.3) blur(1px)' : 'brightness(1) blur(0px)',
                  }}
                  transition={{ 
                    duration: 0.8, 
                    ease: [0.4, 0.0, 0.2, 1] // Smoother cubic-bezier easing
                  }}
                  onMouseEnter={() => setHoveredPanel(service.id)}
                  onMouseLeave={() => setHoveredPanel(null)}
                >
                  {/* Background Image */}
                  <motion.div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: `url('${service.image}')`,
                    }}
                    animate={{
                      scale: isHovered ? 1.1 : 1.05,
                    }}
                    transition={{ 
                      duration: 1.2, 
                      ease: [0.25, 0.46, 0.45, 0.94] // Smooth scale animation
                    }}
                  />

                  {/* Gradient Overlay */}
                  <motion.div 
                    className={`absolute inset-0 bg-gradient-to-t ${service.color}`}
                    animate={{
                      opacity: isHovered ? 0.60 : 0.40,
                    }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                  />

                  {/* Plus Icon */}
                  <motion.div
                    className="absolute top-6 right-6 z-20"
                    animate={{
                      scale: isHovered ? 1.2 : 1,
                      rotate: isHovered ? 45 : 0,
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  >
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                      <FiPlus className="text-white text-sm" />
                    </div>
                  </motion.div>

                  {/* Service Verb */}
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center z-10"
                    animate={{
                      opacity: isHovered ? 0 : 1,
                      y: isHovered ? -20 : 0,
                    }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  >
                    <h3 className="text-white text-3xl md:text-4xl font-bold font-['Montserrat',_sans-serif] tracking-wider transform -rotate-90 whitespace-nowrap">
                      {service.verb}
                    </h3>
                  </motion.div>

                  {/* Expanded Content */}
                  <motion.div
                    className="absolute inset-0 flex flex-col justify-end p-8 z-10"
                    animate={{
                      opacity: isHovered ? 1 : 0,
                      y: isHovered ? 0 : 30,
                    }}
                    transition={{ 
                      duration: 0.6, 
                      delay: isHovered ? 0.3 : 0,
                      ease: "easeOut"
                    }}
                  >
                    <div className="text-white space-y-4">
                      <motion.h4 
                        className="text-2xl md:text-3xl font-bold font-['Montserrat',_sans-serif] leading-tight"
                        animate={{
                          scale: isHovered ? 1 : 0.9,
                        }}
                        transition={{ duration: 0.4, delay: isHovered ? 0.2 : 0 }}
                      >
                        {service.title}
                      </motion.h4>
                      <motion.p 
                        className="text-lg text-gray-200 leading-relaxed max-w-md"
                        animate={{
                          opacity: isHovered ? 1 : 0,
                        }}
                        transition={{ duration: 0.4, delay: isHovered ? 0.4 : 0 }}
                      >
                        {service.description}
                      </motion.p>
                      <motion.button
                        className="shiny-button bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-lg font-semibold border border-white/30 hover:bg-white/30 transition-all duration-300 relative overflow-hidden"
                        animate={{
                          scale: isHovered ? 1 : 0.9,
                          opacity: isHovered ? 1 : 0,
                        }}
                        transition={{ duration: 0.4, delay: isHovered ? 0.5 : 0 }}
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="relative z-10">Learn More</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 transform -skew-x-12 -translate-x-full transition-transform duration-700 hover:translate-x-full hover:opacity-20"></div>
                      </motion.button>
                    </div>
                  </motion.div>

                  {/* Motion Blur Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                    animate={{
                      x: isHovered ? ['-100%', '100%'] : '-100%',
                    }}
                    transition={{
                      duration: isHovered ? 1.5 : 0,
                      repeat: isHovered ? Infinity : 0,
                      ease: "linear"
                    }}
                  />
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="text-center mt-16"
        >
          <motion.button
            className="shiny-button bg-gradient-to-r from-aero-blue to-cyan-400 text-white px-10 py-5 rounded-2xl font-semibold text-lg shadow-2xl relative overflow-hidden font-['Montserrat',_sans-serif]"
            whileHover={{ 
              scale: 1.05, 
              boxShadow: '0 25px 50px rgba(159, 203, 220, 0.4)',
              y: -5 
            }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10">Explore All Services</span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 transform -skew-x-12 -translate-x-full transition-transform duration-700 hover:translate-x-full hover:opacity-30"></div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesPreview;